import React, { useEffect, useState } from 'react';
import { DiputadosTable } from '../components/DiputadosTable';
import { TableAccordion } from '../components/TableAccordion';
import { SearchInput } from '../components/SearchInput';
import { useDebounce } from '../hooks/useDebounce';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Link } from 'react-router-dom';
import { getTop10Atrasos, getTop10Inasistencias, getTop10InasistenciasInjustificadas, getTop10MenosTiempoCamara, getGeneralStats, searchDiputados, DiputadoPublic, DiputadosGeneralStats, Top10DiputadoStat } from '../utils/api';
export function DiputadosStats() {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [searchResults, setSearchResults] = useState<DiputadoPublic[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [atrasosData, setAtrasosData] = useState<Top10DiputadoStat[]>([]);
  const [inasistenciasData, setInasistenciasData] = useState<Top10DiputadoStat[]>([]);
  const [inasistenciasInjustificadasData, setInasistenciasInjustificadasData] = useState<Top10DiputadoStat[]>([]);
  const [menosTiempoCamaraData, setMenosTiempoCamaraData] = useState<Top10DiputadoStat[]>([]);
  const [generalStats, setGeneralStats] = useState<DiputadosGeneralStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const startDate = '2025-09-02';
  const endDate = '2025-12-02';
  useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true);
        setError(null);
        const [atrasos, inasistencias, inasistenciasInjust, menosTiempo, general] = await Promise.all([getTop10Atrasos(startDate, endDate), getTop10Inasistencias(startDate, endDate), getTop10InasistenciasInjustificadas(startDate, endDate), getTop10MenosTiempoCamara(startDate, endDate), getGeneralStats(startDate, endDate)]);
        setAtrasosData(atrasos.data);
        setInasistenciasData(inasistencias.data);
        setInasistenciasInjustificadasData(inasistenciasInjust.data);
        const menosTiempoEnHoras = menosTiempo.data.map(item => ({
          ...item,
          stat_value: item.stat_value / 60,
          stat_total: item.stat_total / 60
        }));
        setMenosTiempoCamaraData(menosTiempoEnHoras);
        setGeneralStats(general);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar datos');
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, []);
  useEffect(() => {
    if (searchQuery.trim()) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
      setSearchResults([]);
    }
  }, [searchQuery]);
  useEffect(() => {
    if (!debouncedSearchQuery.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    async function search() {
      try {
        setIsSearching(true);
        const results = await searchDiputados(debouncedSearchQuery);
        setSearchResults(results.data);
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }
    search();
  }, [debouncedSearchQuery]);
  const totalSesiones = generalStats?.total_sesiones || 0;
  const totalHoras = generalStats ? generalStats.total_sesiones_mins / 60 : 0;
  if (isLoading) {
    return <LoadingSpinner message="Cargando estadísticas..." data-id="element-510" />;
  }
  if (error || !generalStats) {
    return <div className="w-full min-h-screen bg-white flex items-center justify-center px-8 py-24" data-id="element-511">
        <div className="bg-yellow border-brutal border-[#1A1A1A] rounded-none shadow-brutal p-12 max-w-2xl" data-id="element-512">
          <h2 className="font-display text-3xl font-bold text-purple uppercase mb-4" data-id="element-513">
            Error
          </h2>
          <p className="text-lg text-[#1A1A1A] mb-6" data-id="element-514">{error}</p>
          <button onClick={() => window.location.reload()} className="bg-purple border-brutal border-yellow text-white font-display uppercase px-6 py-3 hover:bg-white hover:text-purple transition-colors duration-100" data-id="element-515">
            Reintentar
          </button>
        </div>
      </div>;
  }
  return <div className="w-full min-h-screen bg-white" data-id="element-516">
      {/* Hero Section - Horizontal Layout */}
      <div className="px-4 md:px-6 lg:px-8 py-8" data-id="element-517">
        <div className="max-w-[1400px] mx-auto" data-id="element-518">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8" data-id="element-519">
            {/* Title and Description */}
            <div className="lg:max-w-xl" data-id="element-520">
              <h1 className="font-display text-4xl lg:text-5xl font-bold text-purple uppercase tracking-tight mb-3" data-id="element-521">
                Estadísticas Diputados
              </h1>
              <p className="text-base text-[#1A1A1A] mb-3" data-id="element-522">
                Top 10 de diputados por diferentes métricas de asistencia y
                participación
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 text-purple hover:text-yellow transition-colors duration-100 font-display text-sm uppercase tracking-wide font-bold" data-id="element-523">
                ¿Quieres saber más de nuestra página? →
              </Link>
            </div>

            {/* Search Input */}
            <div className="lg:flex-1 lg:max-w-xl" data-id="element-524">
              <SearchInput value={searchQuery} onChange={setSearchQuery} results={searchResults} isSearching={isSearching} data-id="element-525" />
            </div>
          </div>
        </div>
      </div>

      {/* All Tables - Accordion on Mobile, Regular on Desktop */}
      <div className="px-4 md:px-6 lg:px-8 pb-16" data-id="element-526">
        <div className="max-w-[1400px] mx-auto space-y-8 md:space-y-16" data-id="element-527">
          <TableAccordion title="Top 10 - Menos Tiempo en Sesión" defaultOpen={true} data-id="element-528">
            <DiputadosTable data={menosTiempoCamaraData} title="Top 10 - Menos Tiempo en Sesión" unit="horas" valueColumnName="Tiempo en Sesión" totalValue={totalHoras} averageValue={generalStats.promedio_sesiones_mins_efectivo / 60} averageLabel="Promedio Tiempo en Sesión" startDate={startDate} endDate={endDate} data-id="element-529" />
          </TableAccordion>

          <TableAccordion title="Top 10 - Más Inasistencias Injustificadas" data-id="element-530">
            <DiputadosTable data={inasistenciasInjustificadasData} title="Top 10 - Más Inasistencias Injustificadas" unit="sesiones" valueColumnName="Inasistencias Injustificadas" totalValue={totalSesiones} averageValue={generalStats.promedio_inasistencias_injustificadas} averageLabel="Promedio Inasist. Injustificadas" startDate={startDate} endDate={endDate} data-id="element-531" />
          </TableAccordion>

          <TableAccordion title="Top 10 - Más Atrasos" data-id="element-532">
            <DiputadosTable data={atrasosData} title="Top 10 - Más Atrasos" unit="sesiones" valueColumnName="Atrasos" totalValue={totalSesiones} averageValue={generalStats.promedio_atrasos} averageLabel="Promedio Atrasos" startDate={startDate} endDate={endDate} data-id="element-533" />
          </TableAccordion>

          <TableAccordion title="Top 10 - Más Inasistencias Totales" data-id="element-534">
            <DiputadosTable data={inasistenciasData} title="Top 10 - Más Inasistencias Totales" unit="sesiones" valueColumnName="Inasistencias" totalValue={totalSesiones} averageValue={generalStats.promedio_inasistencias} averageLabel="Promedio Inasistencias" startDate={startDate} endDate={endDate} data-id="element-535" />
          </TableAccordion>
        </div>
      </div>
    </div>;
}